import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { all, call, put, select, spawn, take, takeEvery } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { msas, glsLocation } from 'redux/saga/entities/bound';
import { geodetectMsa } from 'redux/saga/helpers';
import { msaByIdentifier } from 'redux/saga/selectors/msas';
import { deepExtract } from 'helpers/deep';
import { setMsaId, selectMsaId } from 'redux/modules/features/homepage';
import { showModal, hideModal, HIDE_MODAL } from 'redux/modules/modals';
import { ga } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { addFailureNotification } from 'features/Banners/Banners.reducer';
import { doJoinWaitlist as leadgenJoinWaitlist } from 'features/LeadGenForm/LeadGenForm.saga';
import { persistUserLocation } from 'redux/saga/helpers/location';
import { selectUserLocation } from 'redux/modules/locale';
import { userErrorMessages as messages } from 'components/Errors/Error.messages';

const LOCATION_SELECTED = 'classpass/LocationModal/LOCATION_SELECTED';
const JOIN_WAITLIST = 'classpass/LocationModal/JOIN_WAITLIST';
const UPDATE_LOCATION = 'classpass/LocationModal/UPDATE_LOCATION';

export const joinWaitlist = ({ email } = {}) => ({ type: JOIN_WAITLIST, email });
export const locationSelected = msaId => ({ type: LOCATION_SELECTED, msaId });
export const updateLocation = location => ({ type: UPDATE_LOCATION, location });

export function* doJoinWaitlist(client, { email }) {
  yield spawn(segment.track, 'Geo_Detection_Modal', {
    action: 'submit_email',
  });
  yield spawn(ga, 'Geo_Detection_Modal', 'submit_email');
  const userLocation = yield select(selectUserLocation);

  if (userLocation) {
    const { lat, lon } = userLocation;
    yield call(leadgenJoinWaitlist, client, { email, location: { lat, lon } });
  }
}

export function* doUpdateLocation(client, { location: { lat, lon } }) {
  try {
    yield spawn(segment.track, 'Geo_Detection_Modal', {
      action: 'submit_location',
    });
    yield spawn(ga, 'Geo_Detection_Modal', 'submit_location');
    const [location = {}] = yield all([
      call(fetchEntityIfNeeded, glsLocation, [client, lat, lon]),
      call(persistUserLocation, lat, lon),
    ]);
    const { id: msaId, package_purchase: isPurchasable } = location.msa || {};

    if (isPurchasable) {
      yield put(locationSelected(msaId));
      yield put(setMsaId(msaId));
    }
  } catch (err) {
    yield all([
      call(logger.error, 'LocationModal: Could not change location using lat, lon', `${lat}, ${lon}`, err),
      put(addFailureNotification(messages.genericMessage)),
    ]);
  }
}

export function* openLocationModal(modalProps = {}) {
  /* istanbul ignore-next */
  if (__SERVER__) {
    return -1;
  }

  yield put(showModal('LocationModal', modalProps));
  const { msaId } = yield take([HIDE_MODAL, locationSelected().type]);
  yield put(hideModal('LocationModal'));
  return msaId;
}

export function* getMsa(client, modalProps = {}, options = { redirectBackOnMissing: true }) {
  // eslint-disable-next-line prefer-const
  let [allMsas, msa] = yield all([call(fetchEntityIfNeeded, msas, [client]), call(geodetectMsa, client)]);

  const savedMsaId = yield select(selectMsaId);
  if (savedMsaId) {
    return msaByIdentifier(allMsas, savedMsaId);
  }

  if (deepExtract('package_purchase', msa)) {
    return msa;
  }

  /* Required for the pricing and explore pages to function correctly
  when hit directly by users who cannot be geodetected */
  if (__SERVER__) {
    return -1;
  }

  const msaId = yield call(openLocationModal, modalProps);
  msa = msaByIdentifier(allMsas, msaId);

  if (deepExtract('package_purchase', msa)) {
    yield put(setMsaId(msa.id));
    /* istanbul ignore-next */
  } else if (options.redirectBackOnMissing && window.history.length > 2) {
    yield call([window.history, window.history.back]);
    return -1;
  }

  return msa;
}

export function* watchLocationForm(client) {
  yield takeEvery(updateLocation().type, doUpdateLocation, client);
}

export function* watchWaitlistForm(client) {
  yield takeEvery(joinWaitlist().type, doJoinWaitlist, client);
}
